import { createPortal } from 'react-dom';
import { X, Download, FileText, Building2, User, MapPin, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { Paystub } from '../../lib/payrollRuns';
import { useState, useRef } from 'react';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { useBusiness } from '../../contexts/BusinessContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';

interface PaystubViewProps {
  paystub: Paystub;
  resourceInfo: {
    name: string;
    email: string | null;
    avatar_url?: string;
  };
  runNumber: number;
  onClose: () => void;
}

export default function PaystubView({
  paystub,
  resourceInfo,
  runNumber,
  onClose
}: PaystubViewProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { selectedBusiness } = useBusiness();
  const paystubRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleDownload = async () => {
    if (!paystubRef.current) {
      toast.error('Unable to generate PDF. Please try again.');
      return;
    }
    
    try {
      setIsDownloading(true);
      toast.loading('Generating PDF...', { id: 'pdf-download' });
      
      const element = paystubRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      const fileName = `Paystub_${runNumber.toString().padStart(4, '0')}_${resourceInfo.name.replace(/\s+/g, '_')}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF downloaded successfully!', { id: 'pdf-download' });
      setIsDownloading(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-download' });
      setIsDownloading(false);
    }
  };

  const overtimeMinutes = Number(paystub.overtime_minutes) || 0;
  const overtimeEarnings = Number(paystub.overtime_earnings) || 0;
  const timeclockEarnings = Number(paystub.timeclock_earnings) || 0;
  const straightTimeEarnings = Math.max(0, Math.round((timeclockEarnings - overtimeEarnings) * 100) / 100);
  const hoursText = usePayrollHoursText([overtimeMinutes]);
  const overtimeLabel = hoursText(overtimeMinutes);

  const modalContent = (
    <div
      className="fixed inset-0 z-[110000] flex items-center justify-center bg-black/50 p-0 sm:p-4"
      style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={handleClose}
    >
      <div
        className={`flex h-full w-full flex-col bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl ${
          !isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200/60 px-4 py-3 sm:rounded-t-2xl">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-[17px] font-semibold text-gray-900">Paystub</h2>
              <p className="mt-0.5 text-[13px] text-gray-500">Run #{runNumber.toString().padStart(4, '0')}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-blue-600 px-3 text-[13px] font-semibold text-white active:bg-blue-800 disabled:opacity-50 touch-manipulation"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{isDownloading ? 'Generating...' : 'Download PDF'}</span>
              <span className="sm:hidden">PDF</span>
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* PDF-Style Document */}
        <div className="p-4 sm:p-8 bg-gray-50 max-h-[calc(100vh-120px)] sm:max-h-[calc(100vh-200px)] overflow-y-auto">
          <div ref={paystubRef} className="bg-white shadow-lg mx-auto p-4 sm:p-8 md:p-12" style={{ maxWidth: '210mm' }}>
            
            {/* NOT LEGAL BANNER */}
            <div className="bg-red-600 text-white text-center py-2 sm:py-3 mb-4 sm:mb-6 -mt-4 sm:-mt-5 -mx-4 sm:-mx-5">
              <p className="text-sm sm:text-lg font-bold">⚠️ NOT A LEGAL PAYSTUB ⚠️</p>
              <p className="text-xs mt-1">This document is for informational purposes only</p>
            </div>

            {/* Header - Business Info */}
            <div className="border-b-2 sm:border-b-4 border-gray-900 pb-4 sm:pb-6 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">PAYSTUB</h1>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="text-base sm:text-xl font-semibold break-words">{selectedBusiness?.name || 'Business Name'}</span>
                  </div>
                  {selectedBusiness?.address && (
                    <div className="flex items-start gap-2 text-gray-600 mt-2">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-1" />
                      <span className="text-xs sm:text-sm break-words">{selectedBusiness.address}</span>
                    </div>
                  )}
                  {selectedBusiness?.phone && (
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <Phone className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm">{selectedBusiness.phone}</span>
                    </div>
                  )}
                  {selectedBusiness?.email && (
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <Mail className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm break-all">{selectedBusiness.email}</span>
                    </div>
                  )}
                </div>
                <div className="w-full sm:w-auto flex-shrink-0">
                  <div className="bg-gray-900 text-white px-3 sm:px-4 py-2 rounded-lg">
                    <div className="text-xs font-medium">Pay Period</div>
                    <div className="text-sm font-bold mt-1">
                      {format(new Date(paystub.period_start_date), 'MMM d, yyyy')}
                    </div>
                    <div className="text-xs">to</div>
                    <div className="text-sm font-bold">
                      {format(new Date(paystub.period_end_date), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div className="mt-2 text-xs sm:text-sm text-gray-600 text-center sm:text-right">
                    Run #{runNumber.toString().padStart(4, '0')}
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Information */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 border-b-2 border-gray-300 pb-2">
                EMPLOYEE INFORMATION
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Employee Name</div>
                      <div className="text-base font-semibold text-gray-900">{resourceInfo.name}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500">Pay Period</div>
                    <div className="text-sm font-medium text-gray-900">
                      {format(new Date(paystub.period_start_date), 'MMMM d, yyyy')} - {format(new Date(paystub.period_end_date), 'MMMM d, yyyy')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">
                EARNINGS
              </h2>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {timeclockEarnings > 0 && overtimeMinutes > 0 && (
                    <>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 px-4 text-sm text-gray-900">Straight time</td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                          ${straightTimeEarnings.toFixed(2)}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 px-4 text-sm text-gray-900">
                          Overtime ({overtimeLabel})
                          <span className="block text-xs text-orange-600">Included in timeclock pay</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium text-orange-600">
                          ${overtimeEarnings.toFixed(2)}
                        </td>
                      </tr>
                    </>
                  )}
                  {timeclockEarnings > 0 && overtimeMinutes <= 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-3 px-4 text-sm text-gray-900">Timeclock Earnings</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                        ${timeclockEarnings.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {paystub.flat_rate_earnings > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-3 px-4 text-sm text-gray-900">Flat Rate Earnings</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                        ${paystub.flat_rate_earnings.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {paystub.commission_earnings > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-3 px-4 text-sm text-gray-900">Commission Earnings</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                        ${paystub.commission_earnings.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {paystub.bonus_earnings > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-3 px-4 text-sm text-gray-900">Bonus Earnings</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                        ${paystub.bonus_earnings.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  <tr className="bg-gray-100 border-t-2 border-gray-400">
                    <td className="py-3 px-4 text-sm font-bold text-gray-900">Total Gross Earnings</td>
                    <td className="py-3 px-4 text-base text-right font-bold text-gray-900">
                      ${paystub.gross_earnings.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Deductions */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-gray-900 mb-4 border-b-2 border-gray-300 pb-2">
                DEDUCTIONS
              </h2>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 text-sm text-gray-900">Federal Income Tax</td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                      ${paystub.tax_deductions.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 text-sm text-gray-900">Employment Insurance (EI)</td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                      ${paystub.ei_deductions.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 text-sm text-gray-900">Canada Pension Plan (CPP)</td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                      ${paystub.cpp_deductions.toFixed(2)}
                    </td>
                  </tr>
                  {paystub.other_deductions > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-3 px-4 text-sm text-gray-900">Other Deductions</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-gray-900">
                        ${paystub.other_deductions.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  <tr className="bg-gray-100 border-t-2 border-gray-400">
                    <td className="py-3 px-4 text-sm font-bold text-gray-900">Total Deductions</td>
                    <td className="py-3 px-4 text-base text-right font-bold text-red-600">
                      -${paystub.total_deductions.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Net Pay - Highlighted Section */}
            <div className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium opacity-90 mb-1">NET PAY</div>
                  <div className="text-xs opacity-75">
                    Gross ${paystub.gross_earnings.toFixed(2)} - Deductions ${paystub.total_deductions.toFixed(2)}
                  </div>
                </div>
                <div className="text-5xl font-bold">
                  ${paystub.net_pay.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Summary Table */}
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden mb-8">
              <div className="bg-gray-100 px-4 py-3 border-b-2 border-gray-300">
                <h3 className="text-sm font-bold text-gray-900">PAYMENT SUMMARY</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Gross Earnings</div>
                    <div className="text-lg font-bold text-gray-900">${paystub.gross_earnings.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Deductions</div>
                    <div className="text-lg font-bold text-red-600">-${paystub.total_deductions.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Net Pay</div>
                    <div className="text-lg font-bold text-green-600">${paystub.net_pay.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t-2 border-gray-300 pt-6 mt-8">
              <div className="text-center text-xs text-gray-500 space-y-2">
                <p className="font-semibold">This is an official pay statement. Please retain for your records.</p>
                <p>Generated on {format(new Date(), 'MMMM d, yyyy \'at\' h:mm a')}</p>
                <p className="text-gray-400">
                  If you have any questions regarding this paystub, please contact {selectedBusiness?.name || 'your employer'}.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
